import { Check } from "lucide-react";

// ScapeSource ⟷ QuickBooks "connection string". Three states:
//   idle        — dashed grey link, nothing happening
//   connecting  — pulses on both logos, dots travelling along the link
//   connected   — solid green link, QuickBooks logo gets a check badge
// Purely presentational; the panels own the state (see useQboConnectFlow).

const KEYFRAMES = `
@keyframes qbo-travel { 0% { left: -6px; opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { left: calc(100% - 2px); opacity: 0; } }
@keyframes qbo-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(14,165,233,0.45); } 50% { box-shadow: 0 0 0 8px rgba(14,165,233,0); } }
@keyframes qbo-pulse-green { 0%, 100% { box-shadow: 0 0 0 0 rgba(44,160,28,0.45); } 50% { box-shadow: 0 0 0 8px rgba(44,160,28,0); } }
@keyframes qbo-pop { 0% { transform: scale(0); } 60% { transform: scale(1.25); } 100% { transform: scale(1); } }
@keyframes qbo-fill { from { transform: scaleX(0); } to { transform: scaleX(1); } }
`;

function ScapeSourceMark({ pulsing }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center text-white font-extrabold text-[15px] tracking-tight"
        style={pulsing ? { animation: "qbo-pulse 1.4s ease-in-out infinite" } : undefined}
        data-testid="qbo-visual-scapesource-logo"
      >
        SS
      </div>
      <div className="text-[10.5px] font-semibold text-slate-500">ScapeSource</div>
    </div>
  );
}

function QuickBooksMark({ pulsing, connected }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative">
        <div
          className="w-12 h-12 rounded-full bg-[#2CA01C] flex items-center justify-center text-white font-extrabold text-[17px] lowercase tracking-tighter"
          style={pulsing ? { animation: "qbo-pulse-green 1.4s ease-in-out infinite 0.35s" } : undefined}
          data-testid="qbo-visual-quickbooks-logo"
        >
          qb
        </div>
        {connected && (
          <div
            className="absolute -right-1 -bottom-1 w-5 h-5 rounded-full bg-white flex items-center justify-center"
            style={{ animation: "qbo-pop 0.45s ease-out both" }}
            data-testid="qbo-visual-check"
          >
            <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check size={10} strokeWidth={4} className="text-white" />
            </div>
          </div>
        )}
      </div>
      <div className="text-[10.5px] font-semibold text-slate-500">QuickBooks</div>
    </div>
  );
}

function Link({ state }) {
  return (
    <div className="relative flex-1 min-w-[72px] max-w-[160px] h-12 flex items-start pt-[22px]" aria-hidden="true">
      <div className="relative w-full h-[3px]">
        {state === "idle" && <div className="absolute inset-0 border-t-2 border-dashed border-slate-300" />}
        {state === "connecting" && (
          <>
            <div className="absolute inset-0 rounded-full bg-slate-200" />
            {[0, 0.5, 1].map((delay) => (
              <span
                key={delay}
                className="absolute -top-[3px] w-[9px] h-[9px] rounded-full bg-sky-500"
                style={{ animation: `qbo-travel 1.5s ease-in-out ${delay}s infinite` }}
              />
            ))}
          </>
        )}
        {state === "connected" && (
          <div
            className="absolute inset-0 rounded-full bg-emerald-500 origin-left"
            style={{ animation: "qbo-fill 0.6s ease-out both" }}
          />
        )}
      </div>
    </div>
  );
}

const CAPTIONS = {
  idle: "Not connected",
  connecting: null, // supplied by the flow ("Requesting authorization…" etc.)
  connected: "Connected",
};

export default function QboConnectionVisual({ state = "idle", message }) {
  const caption = state === "connecting" ? message || "Connecting…" : CAPTIONS[state];
  return (
    <div className="flex flex-col items-center" data-testid="qbo-connection-visual" data-state={state}>
      <style>{KEYFRAMES}</style>
      <div className="flex items-start justify-center gap-3">
        <ScapeSourceMark pulsing={state === "connecting"} />
        <Link state={state} />
        <QuickBooksMark pulsing={state === "connecting"} connected={state === "connected"} />
      </div>
      <div
        className={`mt-2 text-[12px] font-semibold ${state === "connected" ? "text-emerald-600" : state === "connecting" ? "text-sky-600" : "text-slate-400"}`}
        data-testid="qbo-visual-caption"
        aria-live="polite"
      >
        {caption}
      </div>
    </div>
  );
}
